import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { getLang, t } from "@/lib/i18n";
import { Play, ArrowRight, ArrowLeft, ImageIcon } from "lucide-react";
import { toast } from "sonner";

export default function CategoryDetail() {
  const { code } = useParams();
  const nav = useNavigate();
  const [qs, setQs] = useState([]);
  const [cat, setCat] = useState(null);
  const [lang, setL] = useState(getLang());
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    const h = () => setL(getLang());
    window.addEventListener("lang-change", h);
    return () => window.removeEventListener("lang-change", h);
  }, []);

  useEffect(() => {
    api.get(`/categories/${code}/questions`).then((r) => setQs(r.data));
    api.get("/categories").then((r) => setCat(r.data.find((c) => c.code === code)));
  }, [code]);

  const startMock = async () => {
    setStarting(true);
    try {
      const r = await api.post("/mock-exam/start", { category_code: code });
      sessionStorage.setItem("tvde_exam", JSON.stringify(r.data));
      nav("/mock-exam/" + r.data.attempt_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start exam");
    } finally { setStarting(false); }
  };

  if (!cat) return <Layout><div className="max-w-6xl mx-auto px-6 py-16">Loading...</div></Layout>;

  const name = cat[`name_${lang}`] || cat.name_pt;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-12 fade-in">
        <button onClick={() => nav("/categories")} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-6" data-testid="back-categories">
          <ArrowLeft className="w-4 h-4" /> {t(lang, "categories")}
        </button>

        <div className="flex flex-col sm:flex-row justify-between gap-6 items-start sm:items-end mb-12">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">// grupo {code} · {qs.length} {t(lang, "questions")}</div>
            <h1 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight max-w-2xl">{name}</h1>
          </div>
          <Button size="lg" onClick={startMock} disabled={starting || qs.length < 5} className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 rounded-full h-12 px-6" data-testid="start-mock-btn">
            <Play className="w-4 h-4 mr-2 fill-current" /> {t(lang, "startMock")}
          </Button>
        </div>

        <div className="grid gap-2" data-testid="questions-list">
          {qs.map((q, i) => (
            <div key={q.id} onClick={() => nav(`/questions/${q.id}`)}
              className="group flex items-center gap-4 px-5 py-4 border border-border rounded-md bg-card hover:border-[hsl(var(--accent))] cursor-pointer transition-colors"
              data-testid={`question-row-${i+1}`}
            >
              <div className="font-mono text-xs text-muted-foreground w-10">{String(q.number).padStart(3, "0")}</div>
              <div className="flex-1 text-sm leading-snug">{q.text_pt}</div>
              {q.image_file && <ImageIcon className="w-4 h-4 text-[hsl(var(--accent))] shrink-0" title="Has illustration" data-testid={`question-camera-${q.id}`} />}
              <ArrowRight className="w-4 h-4 opacity-30 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
